import * as React from "react";
import { Alert, Box, FormLabel, Typography, Popover } from "@mui/material";
import IComponentProps, { YupSchema } from "./interface";
import { FormProvider, useForm } from "react-hook-form";
import { t, Trans } from "@lingui/macro";
import Grid from "@mui/material/Grid";
import RadioGroupMolecule from "@molecules/RadioGroup/Simple";
import SwitchMolecule from "@molecules/Switch";
import TextFieldAtom from "@atoms/TextField";
import { useSelector } from "react-redux";
import HelperErrorMolecule from "@molecules/HelperError";
import { SelectMolecule } from "@molecules/AsyncSelect";
import { yupResolver } from "@hookform/resolvers/yup";
import OverlapDetails from "./OverlapDetails";
import ActivitiesService from "@services/ActivitiesService";
import { ratesFeaturesEnabled } from "@utils/features";

const ClienSupplierRatesForm = ({
  model,
  record,
  errors,
  typeId,
  onChange,
  onIsZero,
  onDirtyChange,
  syncToken,
  defaultDailyRate,
  defaultHourlyRate,
}: IComponentProps) => {
  // Custom days toggle, days auto-populate and default-rate prefill are only
  // available when the rates features are enabled; otherwise Days is always
  // manual (custom=true), like before they were introduced.
  const showRates = ratesFeaturesEnabled();
  const [sErrors, setSErrors] = React.useState<any>(errors ? errors : {});
  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLSpanElement | null>(null);
  const [overlapData, setOverlapData] = React.useState<any>(null);
  const formRef = React.useRef<HTMLDivElement>(null);
  const isResetting = React.useRef(false);
  const lastRecordId = React.useRef<string | undefined>(undefined);
  const lastSyncToken = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        if (model?.id) {
          ActivitiesService.overlaps(model.id, "2026-05-01", "2026-09-05").then((res: any) => {
            if (res && !res.hasErrors()) {
              setOverlapData(res.getData());
            }
          });
        }
        observer.disconnect();
      }
    });

    if (formRef.current) {
      observer.observe(formRef.current);
    }
    return () => observer.disconnect();
  }, [model?.id]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLSpanElement>) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
  };

  const isPopoverOpen = Boolean(popoverAnchorEl);
  const popoverId = isPopoverOpen ? 'overlap-popover' : undefined;

  const currencyList = useSelector(
    (state: any) => state.filters.filters.categories?.rates,
  );
  const ratesTypesList = useSelector(
    (state: any) => state.filters.filters.types?.rates,
  );

  React.useEffect(() => {
    setSErrors(errors ? errors : {});
  }, [errors]);

  const formInstance = useForm<any>({
    mode: "onChange",
    reValidateMode: "onSubmit",
    resolver: yupResolver(YupSchema),
  });

  const {
    watch,
    getValues,
    reset,
    setValue,
    formState: { isDirty },
  } = formInstance;

  const isCalculated = watch("is_calculated");
  const projectValue = Number(watch("value"));
  const extraValue = Number(watch("extra"));
  const daysValue = Number(watch("days"));
  const rateValue = Number(watch("rate"));
  const allowanceQuantityValue = Number(watch("allowance_quantity"));
  const allowanceValue = Number(watch("allowance"));
  const hoursValue = Number(watch("hours"));
  const isHours = watch("time_unit") === "hours";
  const isDays = watch("time_unit") === "days";
  const isCustom = Boolean(watch("custom"));
  const total = !isCalculated
    ? projectValue + extraValue
    : rateValue * (isDays ? daysValue : hoursValue) +
    (isHours ? allowanceQuantityValue * allowanceValue : 0) +
    extraValue;

  const activitiesCount =
    (model as any)?.total_activities ??
    (typeof (model as any)?.totalRelActivities === "function"
      ? (model as any).totalRelActivities()
      : ((model as any)?.activities?.length ?? 0));

  React.useEffect(() => {
    // Only reset if:
    // 1. First mount (lastRecordId is undefined)
    // 2. Record ID changed (switching between records)
    // 3. Going from no record to having a record
    // 4. syncToken bumped (e.g. forced re-sync from server after a save)
    const syncTokenChanged =
      syncToken !== undefined && syncToken !== lastSyncToken.current;
    const shouldReset =
      lastRecordId.current === undefined ||
      lastRecordId.current !== record?.id ||
      (!lastRecordId.current && record?.id) ||
      syncTokenChanged;

    if (!shouldReset) {
      return;
    }

    lastRecordId.current = record?.id;
    lastSyncToken.current = syncToken;
    
    isResetting.current = true;
    reset({
      category_id: record?.category_id ?? (currencyList?.[0]?.id ?? ""),
      type_id: typeId,
      id: record?.id ?? "",
      model_id: model?.id,
      is_calculated: Boolean(record?.info?.is_calculated),
      time_qty: record?.info?.time_qty ?? 0,
      time_unit: record?.info?.time_unit ?? "days",
      allowance_quantity: record?.info?.allowance_quantity ?? 0,
      allowance: record?.info?.allowance ?? 0,
      value: record?.value ?? 0,
      rate: record?.info?.rate ?? 0,
      hours: record?.info?.hours ?? 0,
      days: record?.info?.days ?? 0,
      custom: showRates ? record?.custom ?? false : true,
      extra: record?.info?.extra ?? 0,
    });
    setTimeout(() => { isResetting.current = false; }, 100);
  }, [record, currencyList, typeId, syncToken]);

  // Keep the latest callbacks in refs so the (single) watch subscription below
  // always calls the current onChange/onIsZero. Without this it would capture
  // the first render's onChange, which closes over a stale inspection (e.g.
  // total_activities before activities were added), silently writing back 0.
  const onChangeRef = React.useRef(onChange);
  const onIsZeroRef = React.useRef(onIsZero);
  React.useEffect(() => {
    onChangeRef.current = onChange;
    onIsZeroRef.current = onIsZero;
  });

  React.useEffect(() => {
    const watchAll = formInstance.watch((value, { name, type }) => {
      if (isResetting.current) return;
      if (onChangeRef.current) {
        onChangeRef.current(getValues());
      }
      // Check if zero
      const isHours = getValues().time_unit === "hours";
      const isDays = getValues().time_unit === "days";
      const daysValue = getValues().days;
      const hoursValue = getValues().hours;
      const allowanceQuantityValue = getValues().allowance_quantity;
      const allowanceValue = getValues().allowance;
      const totalB = !getValues().is_calculated
        ? Number(getValues().value) + Number(getValues().extra)
        : Number(getValues().rate) * (isDays ? daysValue : hoursValue) +
        (isHours ? allowanceQuantityValue * allowanceValue : 0) +
        getValues().extra;
      if ((!totalB || totalB === 0) && onIsZeroRef.current) {
        onIsZeroRef.current(true);
      } else if (onIsZeroRef.current) {
        onIsZeroRef.current(false);
      }
    });
    return () => watchAll.unsubscribe();
  }, [watch, formInstance, getValues]);

  React.useEffect(() => {
    if (isDirty && onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty]);

  // When the inspector has default rates, swap the prefilled supplier rate to
  // match the selected time unit (daily <-> hourly). Only applies while the
  // value is still the untouched default and the user hasn't marked it custom,
  // so manual overrides are never clobbered.
  const prevTimeUnit = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    const tu = getValues().time_unit;
    if (!showRates) {
      prevTimeUnit.current = tu;
      return;
    }
    if (
      (defaultDailyRate === null || defaultDailyRate === undefined) &&
      (defaultHourlyRate === null || defaultHourlyRate === undefined)
    ) {
      prevTimeUnit.current = tu;
      return;
    }
    if (prevTimeUnit.current === undefined || prevTimeUnit.current === tu) {
      prevTimeUnit.current = tu;
      return;
    }
    if (getValues().custom) {
      prevTimeUnit.current = tu;
      return;
    }
    const prevDefault =
      prevTimeUnit.current === "hours" ? defaultHourlyRate : defaultDailyRate;
    const newDefault = tu === "hours" ? defaultHourlyRate : defaultDailyRate;
    const currentRate = getValues().rate;
    const untouched =
      !currentRate ||
      Number(currentRate) === 0 ||
      (prevDefault !== null &&
        prevDefault !== undefined &&
        Number(currentRate) === Number(prevDefault));
    if (untouched && newDefault !== null && newDefault !== undefined) {
      setValue("rate", newDefault, { shouldDirty: true });
    }
    prevTimeUnit.current = tu;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHours, isDays]);

  // When the day-based rate is not marked custom, the number of days is driven
  // by the inspection's activity count (a new inspection defaults custom=off,
  // so Days mirrors the total activities). Enabling "Custom" hands control back
  // to the user. Mount-time seeding of a new supplier rate is done by the
  // parent, so the reset guard here only matters for post-mount toggles.
  React.useEffect(() => {
    if (isResetting.current || !showRates) return;
    if (!isCalculated || !isDays || isCustom) return;
    if (Number(getValues().days) !== Number(activitiesCount)) {
      setValue("days", activitiesCount, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalculated, isDays, isCustom, activitiesCount]);

  return (
    <Box ref={formRef}>
      <form noValidate>
        <FormProvider {...formInstance}>
          <Grid container spacing={1}>
            <Grid item md={6} xs={12}>
              <SelectMolecule
                control={formInstance.control}
                controlName="category_id"
                formError={sErrors["category_id"]}
                storeCollection={"categories.rates"}
                optionValue={"id"}
                variant={"outlined"}
                label={"Currency"}
                optionLabel={"name"}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <SwitchMolecule
                controlName={"is_calculated"}
                control={formInstance.control}
                label={t`Is calculated`}
              />
            </Grid>
          </Grid>
          {isCalculated && (
            <Box mt={1}>
              <Grid container spacing={1}>
                <Grid item md={6} xs={12}>
                  <FormLabel>
                    <Trans>Time unit</Trans>
                  </FormLabel>
                  <RadioGroupMolecule
                    control={formInstance.control}
                    controlName="time_unit"
                    radios={[
                      {
                        label: "hours",
                        value: "hours",
                      },
                      {
                        label: "days",
                        value: "days",
                      },
                    ]}
                  />
                  <HelperErrorMolecule message={sErrors["time_unit"]} />
                </Grid>
                <Grid item md={6} xs={12}></Grid>
              </Grid>
            </Box>
          )}
          {showRates && isDays && isCalculated && Number(watch("days")) !== activitiesCount && (
            <Box mt={1} mb={2}>
              <Alert severity="warning">
                <Trans>Days not aligned with {activitiesCount} activities</Trans>
              </Alert>
            </Box>
          )}
          <Box mt={1} mb={2} sx={{ display: overlapData?.summary?.groups_count > 0 ? 'block' : 'none' }}>
            <Alert
              severity="warning"
              action={
                <Typography variant="body2" color="primary" sx={{ cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center' }} onClick={handlePopoverOpen}>
                  DETAILS
                </Typography>
              }
              sx={{ alignItems: 'center', '& .MuiAlert-action': { pt: 0, pb: 0, alignItems: 'center' } }}
            >
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ mr: 1 }}><b>Overlap detected</b></Box> {overlapData?.summary?.groups_count || 0}/{activitiesCount} days covered
              </Typography>
            </Alert>
            <Popover
              id={popoverId}
              open={isPopoverOpen}
              anchorEl={popoverAnchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: { width: 750, minHeight: 200, p: 2 }
              }}
            >
              <OverlapDetails 
                onClose={handlePopoverClose} 
                data={overlapData} 
                inspectorName={(model as any)?.user?.full_name || ''} 
              />
            </Popover>
          </Box>
          <Box mt={1}>
            <Grid container spacing={1}>
              <Grid
                item
                md={isCalculated && isDays ? 4 : 6}
                xs={12}
                sx={{ display: !isCalculated ? "none" : "" }}
              >
                <TextFieldAtom
                  InputProps={{ inputProps: { min: 0 } }}
                  type={"number"}
                  controlName={"rate"}
                  variant={"outlined"}
                  formError={sErrors["rate"]}
                  label={
                    typeId === ratesTypesList[0].id ? (
                      <Trans>Client rate</Trans>
                    ) : (
                      <Trans>Supplier rate</Trans>
                    )
                  }
                />
              </Grid>
              <Grid item md={isCalculated && isDays ? 8 : 6} xs={12}>
                {isCalculated && isDays && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ flex: showRates ? "0 0 60%" : "1 1 auto" }}>
                      <TextFieldAtom
                        type={"number"}
                        InputProps={{
                          readOnly: !isCustom,
                          inputProps: { min: 0 },
                        }}
                        controlName={"days"}
                        variant={"outlined"}
                        formError={sErrors["days"]}
                        label={<Trans>Days</Trans>}
                      />
                    </Box>
                    {showRates && (
                      <Box sx={{ flex: "0 0 35%" }}>
                        <SwitchMolecule
                          controlName={"custom"}
                          control={formInstance.control}
                          label={t`Custom`}
                        />
                      </Box>
                    )}
                  </Box>
                )}
                {isCalculated && isHours && (
                  <Box>
                    <TextFieldAtom
                      type={"number"}
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                      controlName={"hours"}
                      variant={"outlined"}
                      formError={sErrors["hours"]}
                      label={<Trans>Hours</Trans>}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box mt={1}>
            <Grid container spacing={1}>
              {isCalculated && isHours && (
                <Grid item md={4} xs={12}>
                  <Box>
                    <TextFieldAtom
                      type={"number"}
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                      controlName={"allowance"}
                      variant={"outlined"}
                      helperText={sErrors["allowance"]}
                      label={
                        typeId === ratesTypesList[0].id ? (
                          <Trans>Client allowance</Trans>
                        ) : (
                          <Trans>Supplier allowance</Trans>
                        )
                      }
                    />
                  </Box>
                </Grid>
              )}
              {isCalculated && isHours && (
                <Grid item md={4} xs={12}>
                  <Box>
                    <TextFieldAtom
                      type={"number"}
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                      controlName={"allowance_quantity"}
                      variant={"outlined"}
                      helperText={sErrors["allowance_quantity"]}
                      label={<Trans>Allowance quantity</Trans>}
                    />
                  </Box>
                </Grid>
              )}
              {!isCalculated && (
                <Grid item md={6} xs={12}>
                  <TextFieldAtom
                    type={"number"}
                    controlName={"value"}
                    variant={"outlined"}
                    InputProps={{ inputProps: { min: 0 } }}
                    helperText={sErrors["value"]}
                    label={<Trans>Project value</Trans>}
                  />
                </Grid>
              )}
              <Grid item md={isCalculated && isHours ? 4 : 6} xs={12}>
                <TextFieldAtom
                  type={"number"}
                  controlName={"extra"}
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText={sErrors["extra"]}
                  variant={"outlined"}
                  label={<Trans>Extra</Trans>}
                />
              </Grid>
            </Grid>
          </Box>
          <Box mt={1} className="text-right">
            <Typography>
              <b>
                Total:
                {total}
              </b>
            </Typography>
          </Box>
        </FormProvider>
      </form>
    </Box>
  );
};

export default ClienSupplierRatesForm;
